<script>
  // Step 2 body: Find Frames. Renders inside a <StepCard>; the parent
  // panel owns the card's number/title/subline/done/warning.
  import { createEventDispatcher, getContext } from "svelte";

  import { api } from "#src/api";
  import { user } from "#src/stores/auth.js";
  import { currentTool, selectedLocations, selectedPages } from "#src/stores/ui.js";
  import { alerts } from "#src/stores/alerts.js";
  import { isScaled, sheetFrames } from "../derive.js";
  import { sheetNumber } from "../sheet-label.js";
  import { plural } from "../labels.js";
  import { locateFramesOnSheet } from "../locate.js";
  import { selectAllIds } from "../selection.js";
  import { forceScaleFor } from "../force-scale.js";
  import ScaleWarning from "../ScaleWarning.svelte";

  export let group;
  export let doc;
  export let pages;
  export let indexedLocations;
  export let visibleIndices;
  export let selectedPageList;
  export let collections;
  export let viewer;
  /** The live canvas frame selection, derived once by FrameXPanel. */
  export let selectedLocationList;

  const dispatch = createEventDispatcher();
  const org = getContext("org");

  let running = false;

  // Names a page for the error alert. The sheet number off the title block is how
  // drafters identify a page, so it wins when there is one; a page without one
  // falls back to its position, the way the chips do. The list form moved to
  // ScaleWarning along with the strip it served, leaving the standalone form,
  // where "page #3" marks 3 as a reference to one page rather than a count.
  $: pageName = (id) => sheetNumber(pages[id]) || `page #${visibleIndices[id] + 1}`;

  // The locator runs on exactly the sheets selected on canvas — never on the
  // whole working set. A sheet without a scale cannot be located, and setting
  // its scale is also what admits it to FrameX, so the unscaled case is a
  // prompt rather than a silent skip.
  $: selectedPageIds = selectedPageList.map((p) => p.id);
  // Listed in page order, never selection order: the warning is read against
  // the drawing set, so "pages 2, 3, and 4" has to match the sequence on
  // screen rather than the order they happened to be clicked. Sorting the
  // filtered copy — selectedPageList is the caller's array and stays untouched.
  $: unscaledSelected = selectedPageIds
    .filter((id) => !isScaled(pages[id]))
    .sort((a, b) => visibleIndices[a] - visibleIndices[b]);
  $: locatableIds = selectedPageIds.filter((id) => isScaled(pages[id]));

  // Frames on the selected sheets, or a frame selection standing on its own. Page
  // and frame selections are mutually exclusive — RightPanelTabs clears one when
  // the other is set — so only one term is ever non-zero, and the OR just means
  // "whichever selection is live". Both terms are needed: Find Frames ends by
  // selecting the frames it created, which clears the page selection, so without
  // the second term the guidance would vanish the instant the frames appeared.
  $: selectedFrameCount = selectedPageIds.reduce((n, id) => n + sheetFrames(indexedLocations[id]).length, 0);
  $: showAdjustHint = selectedFrameCount > 0 || selectedLocationList.length > 0;

  // While the frame tool is armed the secondary button reflects it, and the
  // helper line below it swaps to the armed instruction. This is the only
  // draw-mode affordance now — the canvas hint banner it used to mirror is
  // gone (see DrawHintBanner.svelte).
  $: drawing = $currentTool === "location-collection";

  // Ignoring the scale warning must not quietly locate the scaled half of the
  // selection and skip the rest — that was the bug. Route through the same
  // forced-scale flow a blocked frame placement uses, so the demand is a modal
  // rather than a strip the user already scrolled past.
  //
  // The selection narrows to the pages that need a scale first: Step 1's modal
  // applies to the whole selection, so leaving the scaled pages in it would
  // overwrite their scales. Same move Document.svelte makes with selectOnly on
  // the placement path.
  function handleFind() {
    if (unscaledSelected.length) {
      selectedPages.set(selectAllIds(unscaledSelected));
      forceScaleFor.set({
        pageId: unscaledSelected[0],
        note:
          unscaledSelected.length === 1
            ? "This page needs a scale before frames can be located on it."
            : "These pages need a scale before frames can be located on them.",
      });
      return;
    }
    runLocator(locatableIds);
  }

  async function runLocator(ids) {
    if (running || !ids.length) return;
    running = true;

    const { data } = await api.auth.getSession();
    const token = data?.session?.access_token;

    let added = 0;
    let failed = 0;
    // One list for the whole command. The locator runs a sheet at a time, and
    // every sheet's frames append here rather than being selected as they land,
    // so the run ends with all of them selected together instead of each batch
    // replacing the last.
    const newIds = [];

    for (const pageId of ids) {
      try {
        const result = await locateFramesOnSheet({
          pageId,
          doc,
          group,
          groupId: $group.id,
          user: $user,
          org: $org,
          collections,
          indexedLocations,
          token,
          viewer,
          visibleIndices,
        });
        added += result.added;
        newIds.push(...result.locationIds);
      } catch (err) {
        failed += 1;
        alerts.add({
          type: "error",
          message: `Couldn't locate frames on ${pageName(pageId)}: ${err.message}`,
        });
      }
    }

    running = false;

    // Select everything this command created, so the new frames' boundaries are
    // on screen without the user hunting for them. One write, after every sheet
    // has returned: each sheet's run calls group.update twice, and several
    // Document.svelte handlers clear $selectedLocations, so writing mid-run would
    // sit inside that window. Guarded on length so a run that found nothing
    // leaves the existing selection alone instead of silently clearing it.
    if (newIds.length) selectedLocations.set(selectAllIds(newIds));

    if (added > 0) {
      alerts.add({ type: "ok", message: `Found ${added} frame${plural(added)}.` });
    } else if (!failed) {
      alerts.add({ type: "info", message: "No new frames found." });
    }
  }

  // Arms the app's existing Add Frame Marker tool, and disarms it again — the
  // button is the same control both ways, so the way out is where the way in
  // was. Escape still works and is unchanged.
  //
  // The disarm sets `$currentTool` and nothing else, which is exactly what
  // handleEscape does to the tool: Document.svelte's checkCurrentTool clears
  // `adding` off that change on its own. Deliberately NOT a dispatched event or
  // a shared disarm handler — an earlier "Done drawing" toggle routed through
  // one of those and broke box completion.
  function toggleManual() {
    if (drawing) {
      $currentTool = "select";
      return;
    }
    $currentTool = "location-collection";
    dispatch("click-location-tool");
  }
</script>

<div class="fx-actions">
  <ScaleWarning {pages} {selectedPageList} {visibleIndices} action="frames can be located" />
  <button class="fx-primary-button" disabled={running || !selectedPageIds.length} on:click={handleFind}>
    <!-- The locator is scoped to the canvas selection, so the button states
         that scope. With nothing selected the button is disabled, so it names
         the missing precondition rather than an action it will not perform —
         unlike Build, which stays enabled and so keeps its bare verb. -->
    {running
      ? "Locating…"
      : selectedPageIds.length > 0
        ? `Find frames on (${selectedPageIds.length}) selected page${plural(selectedPageIds.length)}`
        : "Select pages to find frames"}
  </button>

  <!-- The found-count itself lives on the step row, which states it whether or
       not this row is open — repeating it here read as two separate numbers.
       What stays is the guidance, shown only when the live selection actually
       puts a frame in front of you. -->
  {#if showAdjustHint}
    <div class="fx-found">
      <span class="fx-found-title">Adjust frame boundaries.</span>
      <span class="fx-found-copy">
        Drag corners so the entire detail is inside, including mark and dimensions.
      </span>
    </div>
  {/if}

  <!-- Drawing by hand needs no canvas selection: the tool is canvas-wide and
       any scaled sheet accepts a frame. -->
  <button class="fx-secondary-button" class:fx-draw-active={drawing} on:click={toggleManual}>
    {drawing ? "Done adding" : "Add missing frames"}
  </button>
  <!-- Only while armed. At rest the button already says what it does, and a
       standing drag instruction described an action that was not running. -->
  {#if drawing}
    <div class="fx-hint">
      Draw a box around each missing glazing elevation including its mark and dimensions.
    </div>
  {/if}
</div>

<style lang="scss">
  // The shared .fx-secondary-button carries the shape; this is only the armed
  // state. Deliberately NOT the handoff's drawMode orange (#f0983c / #c26a12):
  // framex.scss already rejected the prototype's orange for hand-tagged frames
  // in favour of --fx-manual, and the button that arms hand-tagging has to
  // match the frames it produces — two different oranges for one concept is
  // what this replaced. The border is the fill those markers carry; the text
  // takes the palette's warm tone, since --fx-manual is far too light to read
  // as type.
  .fx-draw-active {
    border-color: var(--fx-manual);
    color: var(--fx-amber-text);
  }

  // The same strip as ScaleWarning's — same box, same amber tokens — so the two
  // read as one kind of object in this step. The text colour sits here and the
  // copy inherits it, and no line-height is declared, matching that rule exactly.
  .fx-found {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 8px;
    border-radius: 4px;
    background: var(--fx-amber-bg);
    border: 1px solid var(--fx-amber-border);
    color: var(--fx-amber-text);
  }

  // The lead line names the task, the copy says how. Same size and colour as the
  // copy — weight alone separates them, so the strip still reads as one object.
  .fx-found-title {
    font-size: 12px;
    font-weight: 600;
  }

  .fx-found-copy {
    font-size: 12px;
  }
</style>
